import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Link as LinkIcon, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const SubmitStoryPage = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('manual');

  // Manual Form State
  const [manualForm, setManualForm] = useState({
    title: '',
    content: '',
    author: '',
    category: 'local-news',
    originalArticleUrl: '',
    originalArticleTitle: ''
  });

  // Semi-Auto Form State
  const [semiAutoForm, setSemiAutoForm] = useState({
    originalArticleUrl: '',
    counterStory: '',
    author: ''
  });

  // Photo upload state
  const [manualPhotos, setManualPhotos] = useState([]);
  const [manualPhotoPreviews, setManualPhotoPreviews] = useState([]);
  const [semiAutoPhotos, setSemiAutoPhotos] = useState([]);
  const [semiAutoPhotoPreviews, setSemiAutoPhotoPreviews] = useState([]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    try {
      toast({
        title: "Processing Submission...",
        description: "Creating your article..."
      });

      let contentWithImages = manualForm.content;

      // Upload photos first and insert them into content
      if (manualPhotos.length > 0) {
        toast({
          title: "Uploading Photos...",
          description: `Uploading ${manualPhotos.length} images...`
        });
        
        try {
          // Upload each photo and collect URLs
          const uploadPromises = manualPhotos.map(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/admin/upload-image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            return response.data.url;
          });

          const uploadedUrls = await Promise.all(uploadPromises);

          // Split content into paragraphs
          const paragraphs = contentWithImages.split('\n').filter(p => p.trim());
          
          if (paragraphs.length > 0 && uploadedUrls && uploadedUrls.length > 0) {
            // Calculate positions to insert images
            const totalPhotos = Math.min(uploadedUrls.length, 4); // Max 4 images
            const insertPositions = [];
            
            if (totalPhotos === 1) {
              insertPositions.push(0); // Insert at beginning
            } else if (totalPhotos === 2) {
              insertPositions.push(0, Math.floor(paragraphs.length / 2));
            } else if (totalPhotos === 3) {
              insertPositions.push(0, Math.floor(paragraphs.length / 3), Math.floor(2 * paragraphs.length / 3));
            } else if (totalPhotos === 4) {
              insertPositions.push(0, Math.floor(paragraphs.length / 4), Math.floor(paragraphs.length / 2), Math.floor(3 * paragraphs.length / 4));
            }

            // Insert images at calculated positions
            const newParagraphs = [...paragraphs];
            for (let i = 0; i < totalPhotos; i++) {
              const url = `${process.env.REACT_APP_BACKEND_URL}${uploadedUrls[i]}`;
              const position = insertPositions[i];
              const imageHtml = `<img src="${url}" alt="Article image ${i + 1}" style="width: 100%; height: auto; margin: 20px 0;" />`;
              newParagraphs.splice(position + i, 0, imageHtml);
            }

            contentWithImages = newParagraphs.join('\n\n');
          }
          
          toast({
            title: "Photos Uploaded!",
            description: `${uploadedUrls.length} images ready`
          });
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          toast({
            title: "Photo Upload Failed",
            description: "Continuing without images...",
            variant: "destructive"
          });
          // Continue anyway - article will be created without images
        }
      }

      const { rapidResponseAPI } = await import('../services/api');
      
      const responseData = {
        submitter_name: manualForm.author,
        submitter_email: manualForm.author.toLowerCase().replace(/\s+/g, '') + '@centralcoasttimes.com',
        original_article_url: manualForm.originalArticleUrl,
        original_article_title: manualForm.originalArticleTitle,
        your_story: contentWithImages,
        key_points: []
      };

      const result = await rapidResponseAPI.create(responseData);

      toast({
        title: "Article Published Successfully!",
        description: "Your article has been created."
      });

      // Navigate to review page
      window.location.href = `/review-article/${result._id || result.id}`;
      
    } catch (error) {
      console.error('Manual submission error:', error);
      let errorMessage = "Failed to submit story";
      
      if (error.response?.data?.detail) {
        // Handle validation errors (array) or simple string
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleSemiAutoSubmit = async (e) => {
    e.preventDefault();
    if (!semiAutoForm.originalArticleUrl || !semiAutoForm.author || !semiAutoForm.counterStory) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      toast({
        title: "Generating Counter-Article...",
        description: "AI is analyzing the original article and creating your counter-narrative. This may take 30-60 seconds."
      });

      const { rapidResponseAPI } = await import('../services/api');
      
      const responseData = {
        submitter_name: semiAutoForm.author,
        submitter_email: semiAutoForm.author.toLowerCase().replace(/\s+/g, '') + '@centralcoasttimes.com',
        original_article_url: semiAutoForm.originalArticleUrl,
        original_article_title: 'Article from Express Advocate',
        your_story: semiAutoForm.counterStory,
        key_points: []
      };

      const result = await rapidResponseAPI.create(responseData);
      
      // Upload photos if any
      if (semiAutoPhotos.length > 0) {
        toast({
          title: "Uploading Photos...",
          description: "Attaching your evidence to the article"
        });
        
        const { evidenceAPI } = await import('../services/api');
        const articleId = result._id || result.id;
        
        try {
          await evidenceAPI.uploadMultiple(
            semiAutoPhotos, 
            articleId, 
            semiAutoForm.author, 
            semiAutoForm.author.toLowerCase().replace(/\s+/g, '') + '@centralcoasttimes.com'
          );
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          // Continue anyway - article is created
        }
      }

      toast({
        title: "Counter-Article Generated!",
        description: "Review the AI-generated draft and publish when ready."
      });

      // Navigate to review page
      window.location.href = `/review-article/${result._id || result.id}`;
      
    } catch (error) {
      console.error('Semi-auto submission error:', error);
      let errorMessage = "Failed to generate counter-article";
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-serif font-bold mb-4">
          Submit Your Story
        </h1>
        <p className="text-gray-600 text-lg">
          Choose how you'd like to submit your article. Write it yourself or get AI assistance.
        </p>
      </div>

      {/* Submission Methods */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="manual" className="flex items-center gap-1 text-sm">
            <Upload size={14} />
            Write Myself
          </TabsTrigger>
          <TabsTrigger value="semi-auto" className="flex items-center gap-1 text-sm">
            <Sparkles size={14} />
            AI: URL
          </TabsTrigger>
          <TabsTrigger value="auto" className="flex items-center gap-1 text-sm">
            <Sparkles size={14} />
            AI: Details
          </TabsTrigger>
        </TabsList>

        {/* Manual Submission */}
        <TabsContent value="manual">
          <Card>
            <CardHeader>
              <CardTitle>Submit Your Article</CardTitle>
              <CardDescription>
                ✍️ Write and publish your article with images
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="author">Your Name *</Label>
                    <Input
                      id="author"
                      value={manualForm.author}
                      onChange={(e) => setManualForm({...manualForm, author: e.target.value})}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      value={manualForm.category}
                      onChange={(e) => setManualForm({...manualForm, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      required
                    >
                      <option value="local-news">Local News</option>
                      <option value="politics">Politics</option>
                      <option value="sports">Sports</option>
                      <option value="entertainment">Entertainment</option>
                      <option value="lifestyle">Lifestyle</option>
                      <option value="food-drinks">Food & Drinks</option>
                      <option value="opinion">Opinion</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="title">Article Title *</Label>
                  <Input
                    id="title"
                    value={manualForm.title}
                    onChange={(e) => setManualForm({...manualForm, title: e.target.value})}
                    placeholder="Your Response Title"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="originalArticleUrl">Source Article URL (optional)</Label>
                  <Input
                    id="originalArticleUrl"
                    type="url"
                    value={manualForm.originalArticleUrl}
                    onChange={(e) => setManualForm({...manualForm, originalArticleUrl: e.target.value})}
                    placeholder="https://... (optional reference link)"
                  />
                </div>

                <div>
                  <Label htmlFor="originalArticleTitle">Original Article Title *</Label>
                  <Input
                    id="originalArticleTitle"
                    value={manualForm.originalArticleTitle}
                    onChange={(e) => setManualForm({...manualForm, originalArticleTitle: e.target.value})}
                    placeholder="The title of the Express Advocate article"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="content">Your Story *</Label>
                  <Textarea
                    id="content"
                    value={manualForm.content}
                    onChange={(e) => setManualForm({...manualForm, content: e.target.value})}
                    placeholder="Write your article here. The first photo you upload will be the main image. Up to 3 additional photos will be inserted into your article."
                    rows={12}
                    required
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Write your counter-narrative. Be factual, provide evidence, and stay professional.
                  </p>
                </div>

                <div>
                  <Label htmlFor="manual-photos">Upload Photos/Evidence (Optional)</Label>
                  <input
                    id="manual-photos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setManualPhotos(files);
                      const previews = files.map(file => URL.createObjectURL(file));
                      setManualPhotoPreviews(previews);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  <p className="text-sm font-semibold text-blue-600 mt-2">
                    📸 You can select MULTIPLE IMAGES at once (hold Ctrl/Cmd while clicking) - up to 5 images
                  </p>
                  
                  {manualPhotoPreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {manualPhotoPreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg border" />
                          <button
                            type="button"
                            onClick={() => {
                              const newPhotos = [...manualPhotos];
                              const newPreviews = [...manualPhotoPreviews];
                              newPhotos.splice(index, 1);
                              newPreviews.splice(index, 1);
                              setManualPhotos(newPhotos);
                              setManualPhotoPreviews(newPreviews);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-sm font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-6" size="lg">
                  📤 PUBLISH ARTICLE
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Semi-Auto Submission */}
        <TabsContent value="semi-auto">
          <Card>
            <CardHeader>
              <CardTitle>AI-Powered: Respond to Article URL</CardTitle>
              <CardDescription>
                🤖 AI-POWERED: Paste any article URL and AI will analyze it and help you write a response article
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSemiAutoSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="semiAutoUrl">Article URL to Respond To *</Label>
                  <Input
                    id="semiAutoUrl"
                    type="url"
                    value={semiAutoForm.originalArticleUrl}
                    onChange={(e) => setSemiAutoForm({...semiAutoForm, originalArticleUrl: e.target.value})}
                    placeholder="https://..."
                    required
                  />
                  <p className="text-sm text-blue-600 font-semibold mt-2">
                    🤖 AI will automatically analyze the article and help you write your response
                  </p>
                </div>

                <Button type="submit" className="w-full" size="lg">
                  Fetch Article
                </Button>

                <div className="border-t pt-6">
                  <div>
                    <Label htmlFor="semiAutoAuthor">Your Name *</Label>
                    <Input
                      id="semiAutoAuthor"
                      value={semiAutoForm.author}
                      onChange={(e) => setSemiAutoForm({...semiAutoForm, author: e.target.value})}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="counterStory">Your Story/Response *</Label>
                    <Textarea
                      id="counterStory"
                      value={semiAutoForm.counterStory}
                      onChange={(e) => setSemiAutoForm({...semiAutoForm, counterStory: e.target.value})}
                      placeholder="Share your perspective, additional context, or response to the article..."
                      rows={10}
                    />
                    <p className="text-sm text-blue-600 mt-2">
                      🤖 AI will use this to write a professional article for you
                    </p>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="semi-auto-photos">Upload Photos/Evidence (Optional)</Label>
                    <input
                      id="semi-auto-photos"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setSemiAutoPhotos(files);
                        const previews = files.map(file => URL.createObjectURL(file));
                        setSemiAutoPhotoPreviews(previews);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <p className="text-sm font-semibold text-blue-600 mt-2">
                      📸 You can select MULTIPLE IMAGES at once (hold Ctrl/Cmd while clicking) - up to 5 images
                    </p>
                    
                    {semiAutoPhotoPreviews.length > 0 && (
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        {semiAutoPhotoPreviews.map((preview, index) => (
                          <div key={index} className="relative">
                            <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg border" />
                            <button
                              type="button"
                              onClick={() => {
                                const newPhotos = [...semiAutoPhotos];
                                const newPreviews = [...semiAutoPhotoPreviews];
                                newPhotos.splice(index, 1);
                                newPreviews.splice(index, 1);
                                setSemiAutoPhotos(newPhotos);
                                setSemiAutoPhotoPreviews(newPreviews);
                              }}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-sm font-bold"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-6 mt-6" size="lg">
                  📤 PUBLISH ARTICLE WITH AI
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto Submission */}
        <TabsContent value="auto">
          <Card>
            <CardHeader>
              <CardTitle>AI-Powered: From Story Details</CardTitle>
              <CardDescription>
                🤖 AI-POWERED: Share key details and AI will write a professional article for you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Sparkles className="text-blue-600" size={20} />
                  How AI Assists You
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex gap-3">
                    <span className="font-bold text-blue-600">1.</span>
                    <span>You provide the basic details: who, what, when, where, why</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-blue-600">2.</span>
                    <span>AI writes a professional article based on your information</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-blue-600">3.</span>
                    <span>You review, edit, and publish when ready</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-blue-600">4.</span>
                    <span>Your byline, your story, your control</span>
                  </li>
                </ol>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="font-bold text-lg mb-3 text-green-900">
                  ✓ System Status: Active
                </h3>
                <p className="text-gray-700 mb-3">
                  Our automated monitoring system is currently active and checking for new articles daily.
                </p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Last Check:</span>
                    <span className="font-medium">Today at 6:00 AM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Next Check:</span>
                    <span className="font-medium">Tomorrow at 6:00 AM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Articles Monitored:</span>
                    <span className="font-medium">247 this month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Counter-Stories Published:</span>
                    <span className="font-medium">12 this month</span>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  This system runs automatically. No action needed from you!
                </p>
                <p className="text-sm text-gray-500">
                  If you want to submit a specific story immediately, use the Manual or URL-Based submission methods above.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubmitStoryPage;